import { useCallback } from "react"
import { useSelector } from "react-redux"

import type { PowerlinePlotReqConfig } from "types/dashboard.types"
import type { DateRange } from "types/date.types"
import type { TelemetryFetcher, QualityFetcher } from "types/telemetries.types"

import {
  useLazyPowerlineTelemetryQuery,
  useLazyPowerlineTelemetryQualityQuery,
} from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import {
  useMultiEntityTelemetry,
  type UseMultiEntityTelemetryReturn,
} from "./useMultiEntityTelemetry"

interface UseMultiPowerlineTelemetryProps {
  powerlineIds: number[]
  reqConfig: PowerlinePlotReqConfig
  dateRange: DateRange | null
  isDateRangeValid: boolean
}

export const useMultiPowerlineTelemetry = ({
  powerlineIds,
  reqConfig,
  dateRange,
  isDateRangeValid,
}: UseMultiPowerlineTelemetryProps): UseMultiEntityTelemetryReturn => {
  const orgId = useSelector(selectCurrentOrgId)
  const [fetchPowerlineTelemetry] = useLazyPowerlineTelemetryQuery()
  const [fetchPowerlineTelemetryQuality] = useLazyPowerlineTelemetryQualityQuery()
  const { measurement } = reqConfig

  const fetchTelemetry = useCallback<TelemetryFetcher>(
    (id, params, preferCacheValue) =>
      fetchPowerlineTelemetry(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        preferCacheValue,
      ).unwrap(),
    [fetchPowerlineTelemetry, orgId, measurement],
  )

  const fetchTelemetryQuality = useCallback<QualityFetcher>(
    (id, params) =>
      fetchPowerlineTelemetryQuality(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        true,
      ).unwrap(),
    [fetchPowerlineTelemetryQuality, orgId, measurement],
  )

  return useMultiEntityTelemetry({
    ids: powerlineIds,
    reqConfig,
    dateRange,
    isDateRangeValid,
    enabled: orgId !== undefined,
    fetchTelemetry,
    fetchTelemetryQuality,
  })
}
